import { type FormEvent, type ReactNode, useState } from 'react'
import { Head, Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import PostForm, { type PostFormData } from '@/components/dashboard/PostForm'
import SavedDialog from '@/components/SavedDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { LinkableEvent, Post } from '@/types'

type Props = {
  post: Post
  currently_pinned_id: string | null
  linkable_events: LinkableEvent[]
  errors?: Partial<Record<'body' | 'image' | 'linked_event_id', string[]>>
}

export default function DashboardPostsEdit({ post, currently_pinned_id, linkable_events, errors }: Props) {
  const [savedOpen, setSavedOpen] = useState(false)
  const form = useForm<PostFormData>({
    body: post.body,
    image: null,
    remove_image: false,
    image_preview_url: post.image_url,
    linked_event_id: post.linked_event_id,
    pinned: !!post.pinned_at,
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    form.transform(({ image_preview_url: _ignored, pinned: _ignoredPin, ...rest }) => ({ post: rest }))
    form.patch(`/dashboard/posts/${post.id}`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => setSavedOpen(true),
    })
  }

  return (
    <>
      <Head title="Edit post" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <Button asChild variant="ghost" size="sm" className="mb-4">
          <Link href="/dashboard/posts">
            <ArrowLeft className="size-4" />
            Back to posts
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>Edit post</CardTitle>
          </CardHeader>
          <CardContent>
            <PostForm
              form={form}
              linkableEvents={linkable_events}
              currentlyPinnedId={currently_pinned_id}
              submitLabel="Save changes"
              onSubmit={handleSubmit}
              errors={errors}
              showPinControl={false}
            />
          </CardContent>
        </Card>
      </div>
      <SavedDialog open={savedOpen} onOpenChange={setSavedOpen} title="Post saved" />
    </>
  )
}

DashboardPostsEdit.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
